import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
    AppBar, Toolbar, IconButton, List, ListItem, ListItemButton, ListItemIcon,
    ListItemText, Button, DialogActions, DialogContent, Typography, Box, Tooltip,
    styled, TextField, Alert, LinearProgress, Dialog, Tabs, Tab, Menu, MenuItem, Breadcrumbs, Link,
    ToggleButtonGroup, ToggleButton
} from '@mui/material';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import FolderIcon from '@mui/icons-material/Folder';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import SortByAlphaIcon from '@mui/icons-material/SortByAlpha';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import SharedFileBrowser, { SharedFileList, SharedFile } from './SharedFileBrowser';
import { socket, URL } from '../socket';
import useStore from '../store';
import { IoEvent } from '../peregrineFlowInterfaces';

export type FileEntry = {
    type: string
    mimeType?: string
    name: string
    path: string
}

export type FileList = {
    files: FileEntry[]
    parent?: FileEntry
    current?: FileEntry
}

export type FileProgress = {
    filename: string
    loaded: number
    total: number
}

export type FileManagerMode = 'select' | 'manage';

type FileManagerProps = {
    open: boolean;
    onClose: () => void;
    mode?: FileManagerMode;
    onSelectFile?: (file: FileEntry | SharedFile) => void;
    title?: string;
}

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

const FileManager = ({ open, onClose, mode = 'manage', onSelectFile, title = "File Manager" }: FileManagerProps) => {
    const [tabValue, setTabValue] = useState(0);
    const [fileList, setFileList] = useState<FileList>({ files: [], parent: undefined });
    const [sharedFiles, setSharedFiles] = useState<SharedFileList>({});
    const [isLoading, setIsLoading] = useState(false);
    const [selectedItem, setSelectedItem] = useState<FileEntry>();
    const [showCreateFolder, setShowCreateFolder] = useState(false);
    const [newFolderName, setNewFolderName] = useState("");
    const [renameDialogOpen, setRenameDialogOpen] = useState(false);
    const [renameValue, setRenameValue] = useState("");
    const [renameItem, setRenameItem] = useState<FileEntry | null>(null);
    const [sortBy, setSortBy] = useState<'name' | 'type'>('name');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

    const token = useStore((state) => state.token);
    const isGuest = !token;

    // Context menu state
    const [contextMenu, setContextMenu] = useState<{
        mouseX: number;
        mouseY: number;
        item: FileEntry;
    } | null>(null);

    const loadFiles = useCallback((path?: string) => {
        setIsLoading(true);
        socket.emit('io', { event_type: "list_files", data: path } as IoEvent);
    }, []);

    const loadSharedFiles = useCallback(() => {
        setIsLoading(true);
        socket.emit('io', { event_type: "list_shared_files" } as IoEvent);
    }, []);

    useEffect(() => {
        if (open) {
            loadFiles();
            if (tabValue === 1) {
                loadSharedFiles();
            }
        } else {
            setSelectedItem(undefined);
            setContextMenu(null);
        }
    }, [open, tabValue, loadFiles, loadSharedFiles]);

    useEffect(() => {
        const onIoEvent = (data: IoEvent) => {
            if (data.event_type === 'list_files') {
                setFileList(data.data as FileList);
                setIsLoading(false);
            } else if (data.event_type === 'list_shared_files') {
                setSharedFiles(data.data as SharedFileList);
                setIsLoading(false);
            } else if (data.event_type === 'create_folder') {
                // create_folder doesn't trigger list_files, so we need to reload
                // Use functional update to get current state without adding fileList to deps
                setFileList(currentFileList => {
                    loadFiles(currentFileList.current?.path);
                    return currentFileList;
                });
            }
        };

        socket.on('io', onIoEvent);

        return () => {
            socket.off('io', onIoEvent);
        };
    }, [loadFiles]);

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
        setSelectedItem(undefined);
    };

    const handleEnterFolder = (folder: FileEntry) => {
        loadFiles(folder.path);
        setSelectedItem(undefined);
    };

    const handleCreateFolder = () => {
        if (!newFolderName) return;
        const path = fileList.current ? fileList.current.path : "";
        socket.emit('io', {
            event_type: "create_folder",
            data: { path: path, name: newFolderName }
        } as IoEvent);
        setShowCreateFolder(false);
        setNewFolderName("");
    };

    const handleUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!event.target.files || !event.target.files[0]) return;
        const file = event.target.files[0];
        const path = fileList.current ? fileList.current.path : "";

        // We need to use FormData for file upload, but the socket implementation uses a different approach?
        // The server code uses `request.files` in the `/upload` route.
        // So we should use fetch to upload.

        const formData = new FormData();
        formData.append('file', file);
        formData.append('destination', path);
        formData.append('token', token || '');

        setIsLoading(true);

        // We need to know the server URL. imported from socket.ts
        fetch(`${URL}/upload`, {
            method: 'POST',
            body: formData,
        })
        .then(response => {
            if (response.ok) {
                loadFiles(path);
            } else {
                console.error("Upload failed");
                setIsLoading(false);
            }
        })
        .catch(error => {
            console.error("Upload error", error);
            setIsLoading(false);
        });
    };

    const handleDownload = (entry: FileEntry) => {
        if (!token) return;
        const downloadUrl = `${URL}/download?token=${token}&kind=file&file=${encodeURIComponent(entry.path)}`;
        window.open(downloadUrl, '_blank');
    };

    const handleDelete = (entry: FileEntry) => {
        if (window.confirm(`Are you sure you want to delete ${entry.name}?`)) {
            setIsLoading(true);
            socket.emit('io', { event_type: "delete_file", data: entry.path } as IoEvent);
        }
        setContextMenu(null);
    };

    const handleRename = () => {
        if (!renameItem || !renameValue) return;
        setIsLoading(true);
        socket.emit('io', {
            event_type: "rename_file",
            data: { oldPath: renameItem.path, newName: renameValue }
        } as IoEvent);
        setRenameDialogOpen(false);
        setRenameItem(null);
    };

    const handleContextMenu = (event: React.MouseEvent, entry: FileEntry) => {
        event.preventDefault();
        setContextMenu(
            contextMenu === null
                ? {
                    mouseX: event.clientX + 2,
                    mouseY: event.clientY - 6,
                    item: entry,
                }
                : null,
        );
    };

    // Sort files: folders first, then by selected criteria
    const sortedFiles = useMemo(() => {
        const files = [...fileList.files];
        return files.sort((a, b) => {
            // Always put folders before files
            if (a.type !== b.type) {
                return a.type === 'folder' ? -1 : 1;
            }

            let comparison = 0;
            if (sortBy === 'name') {
                comparison = a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
            } else if (sortBy === 'type') {
                const extA = a.name.includes('.') ? a.name.split('.').pop() || '' : '';
                const extB = b.name.includes('.') ? b.name.split('.').pop() || '' : '';
                comparison = extA.localeCompare(extB, undefined, { sensitivity: 'base' }) || a.name.localeCompare(b.name);
            }

            return sortDirection === 'asc' ? comparison : -comparison;
        });
    }, [fileList.files, sortBy, sortDirection]);

    const handleCloseContextMenu = () => {
        setContextMenu(null);
    };

    const renderMyFiles = () => {
        if (isGuest) {
            return <Alert severity="info">File management is disabled for guest users.</Alert>;
        }

        const pathParts = (fileList.current?.path || "").split('/').filter(Boolean);

        return (
            <>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, justifyContent: 'space-between' }}>
                    <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
                        <Link
                            underline="hover"
                            color="inherit"
                            onClick={() => loadFiles()}
                            sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                        >
                            <FolderIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                            Home
                        </Link>
                        {pathParts.map((part, index) => {
                            const path = pathParts.slice(0, index + 1).join('/');
                            const isLast = index === pathParts.length - 1;
                            return isLast ? (
                                <Typography key={path} color="text.primary">
                                    {part}
                                </Typography>
                            ) : (
                                <Link
                                    key={path}
                                    underline="hover"
                                    color="inherit"
                                    onClick={() => loadFiles(path)}
                                    sx={{ cursor: 'pointer' }}
                                >
                                    {part}
                                </Link>
                            );
                        })}
                    </Breadcrumbs>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ToggleButtonGroup
                            size="small"
                            value={sortBy}
                            exclusive
                            onChange={(_, value) => value && setSortBy(value)}
                            aria-label="sort by"
                        >
                            <ToggleButton value="name" aria-label="sort by name">
                                <Tooltip title="Sort by name"><SortByAlphaIcon fontSize="small" /></Tooltip>
                            </ToggleButton>
                            <ToggleButton value="type" aria-label="sort by type">
                                <Tooltip title="Sort by type"><InsertDriveFileIcon fontSize="small" /></Tooltip>
                            </ToggleButton>
                        </ToggleButtonGroup>
                        <Tooltip title={sortDirection === 'asc' ? 'Ascending' : 'Descending'}>
                            <IconButton
                                size="small"
                                onClick={() => setSortDirection(d => d === 'asc' ? 'desc' : 'asc')}
                            >
                                <ArrowDownwardIcon
                                    fontSize="small"
                                    sx={{ transform: sortDirection === 'asc' ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}
                                />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Create new folder">
                            <IconButton onClick={() => setShowCreateFolder(true)}>
                                <CreateNewFolderIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Upload file">
                            <IconButton component="label">
                                <VisuallyHiddenInput type="file" onChange={handleUpload} />
                                <UploadFileIcon />
                            </IconButton>
                        </Tooltip>
                        {fileList.parent && (
                            <Tooltip title="Go Up">
                                <IconButton onClick={() => handleEnterFolder(fileList.parent!)}>
                                    <ArrowUpwardIcon />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                </Box>

                {showCreateFolder && (
                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                        <TextField
                            size="small"
                            label="Folder Name"
                            value={newFolderName}
                            onChange={(e) => setNewFolderName(e.target.value)}
                        />
                        <Button variant="contained" onClick={handleCreateFolder}>Create</Button>
                        <Button onClick={() => setShowCreateFolder(false)}>Cancel</Button>
                    </Box>
                )}

                {isLoading && <LinearProgress />}

                <List dense>
                    {sortedFiles.map((entry) => (
                        <ListItem
                            key={entry.path}
                            disablePadding
                            secondaryAction={
                                <IconButton edge="end" onClick={(e) => handleContextMenu(e, entry)}>
                                    <MoreVertIcon />
                                </IconButton>
                            }
                        >
                            <ListItemButton
                                selected={selectedItem?.path === entry.path}
                                onClick={() => setSelectedItem(entry)}
                                onDoubleClick={() => entry.type === 'folder' ? handleEnterFolder(entry) : null}
                            >
                                <ListItemIcon>
                                    {entry.type === 'folder' ? <FolderIcon /> : <InsertDriveFileIcon />}
                                </ListItemIcon>
                                <ListItemText primary={entry.name} />
                            </ListItemButton>
                        </ListItem>
                    ))}
                </List>

                <Menu
                    open={contextMenu !== null}
                    onClose={handleCloseContextMenu}
                    anchorReference="anchorPosition"
                    anchorPosition={
                        contextMenu !== null
                            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
                            : undefined
                    }
                >
                    <MenuItem onClick={() => {
                        if (contextMenu) {
                            setRenameValue(contextMenu.item.name);
                            setRenameItem(contextMenu.item);
                            setRenameDialogOpen(true);
                            handleCloseContextMenu();
                        }
                    }}>
                        <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
                        Rename
                    </MenuItem>
                    <MenuItem onClick={() => contextMenu && handleDownload(contextMenu.item)} disabled={contextMenu?.item.type !== 'file'}>
                        <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
                        Download
                    </MenuItem>
                    <MenuItem onClick={() => contextMenu && handleDelete(contextMenu.item)}>
                        <ListItemIcon><DeleteIcon fontSize="small" /></ListItemIcon>
                        Delete
                    </MenuItem>
                </Menu>

                <Dialog open={renameDialogOpen} onClose={() => setRenameDialogOpen(false)}>
                    <DialogContent>
                        <TextField
                            label="New Name"
                            value={renameValue}
                            onChange={(e) => setRenameValue(e.target.value)}
                            fullWidth
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setRenameDialogOpen(false)}>Cancel</Button>
                        <Button onClick={handleRename}>Rename</Button>
                    </DialogActions>
                </Dialog>
            </>
        );
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <AppBar position="static" color="default" elevation={0}>
                <Toolbar variant="dense">
                    <Typography variant="h6" sx={{ flexGrow: 1 }}>
                        {title}
                    </Typography>
                    <Tabs value={tabValue} onChange={handleTabChange}>
                        <Tab label="My Files" />
                        <Tab label="Shared Files" />
                    </Tabs>
                </Toolbar>
            </AppBar>

            <DialogContent sx={{ minHeight: 400 }}>
                {tabValue === 0 && renderMyFiles()}
                {tabValue === 1 && (
                    <SharedFileBrowser
                        title=""
                        sharedFiles={sharedFiles}
                        show={true}
                        onClose={() => {}}
                        onSelectFile={(file) => {
                            if (mode === 'select') {
                                onSelectFile?.(file);
                                onClose();
                            }
                        }}
                        isLoading={isLoading}
                        embedded={true} // Need to update SharedFileBrowser to support embedded mode
                    />
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>Close</Button>
                {mode === 'select' && tabValue === 0 && (
                    <Button
                        variant="contained"
                        disabled={!selectedItem && !fileList.current}
                        onClick={() => {
                            const item = selectedItem || fileList.current;
                            if (item) {
                                onSelectFile?.(item);
                                onClose();
                            }
                        }}
                    >
                        Select {selectedItem ? `"${selectedItem.name}"` : "Current Folder"}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default FileManager;
